import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [needs2fa, setNeeds2fa] = useState(false);
  const [totpCode, setTotpCode] = useState("");

  const handle2faSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await api.post("/auth/login/2fa", { code: totpCode });
      setUser(res.data);
      navigate("/dashboard", { replace: true, state: { user: res.data } });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      if (res.data?.requires_2fa) {
        setNeeds2fa(true);
        return;
      }
      setUser(res.data);
      navigate("/dashboard", { replace: true, state: { user: res.data } });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      <div className="hidden lg:flex lg:w-1/2 bg-brand-navy text-white flex-col justify-between p-12">
        <Link to="/" data-testid="login-logo-link" aria-label="NEXA — Accueil">
          <Logo light />
        </Link>
        <div>
          <h1 className="text-4xl font-extrabold leading-tight mb-4">
            Le talent n'a pas de{" "}
            <span className="text-brand-turquoise">frontières</span>
          </h1>
          <p className="text-white/60 text-lg max-w-md">
            La plateforme de recrutement international qui connecte les talents aux opportunités.
          </p>
        </div>
        <p className="text-white/40 text-sm">© 2026 NEXA — International Recruitment Platform</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-10">
            <Logo />
          </div>
          <h2 data-testid="login-heading" className="text-2xl font-bold text-brand-navy mb-2">
            Connexion
          </h2>
          <p className="text-slate-500 mb-8">
            Accédez à votre espace candidat, employeur ou recruteur.
          </p>

          {error && (
            <div data-testid="login-error" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              {error}
            </div>
          )}

          {needs2fa ? (
            <form onSubmit={handle2faSubmit} className="space-y-4">
              <div>
                <label htmlFor="login-2fa" className="block text-sm font-medium text-brand-navy mb-1">
                  Code de vérification
                </label>
                <input
                  id="login-2fa"
                  data-testid="login-2fa-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-brand-turquoise focus:border-transparent"
                  placeholder="123456"
                />
                <p className="text-xs text-slate-400 mt-2">
                  Saisissez le code à 6 chiffres de votre application d'authentification, ou un code de secours.
                </p>
              </div>
              <button
                data-testid="login-2fa-submit-button"
                type="submit"
                disabled={submitting}
                className="w-full bg-brand-navy text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-blue transition-colors duration-200 disabled:opacity-50"
              >
                {submitting ? "Vérification…" : "Vérifier"}
              </button>
              <button
                data-testid="login-2fa-back"
                type="button"
                onClick={() => { setNeeds2fa(false); setTotpCode(""); setError(""); }}
                className="w-full text-sm text-slate-500 hover:text-brand-navy"
              >
                ← Retour à la connexion
              </button>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-brand-navy mb-1">
                Adresse courriel
              </label>
              <input
                id="login-email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-turquoise focus:border-transparent"
                placeholder="vous@exemple.com"
              />
            </div>
            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-brand-navy mb-1">
                Mot de passe
              </label>
              <input
                id="login-password"
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-turquoise focus:border-transparent"
                placeholder="••••••••"
              />
            </div>
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full bg-brand-navy text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-blue transition-colors duration-200 disabled:opacity-50"
            >
              {submitting ? "Connexion…" : "Se connecter"}
            </button>
          </form>
          )}

          <div className="flex items-center justify-between mt-4 text-sm">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-slate-500 hover:text-brand-navy">
              Mot de passe oublié ?
            </Link>
            <Link to="/register" data-testid="register-link" className="font-semibold text-brand-turquoise hover:underline">
              Créer un compte
            </Link>
          </div>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400">ou</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <button
            data-testid="google-signin-button"
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 border border-slate-300 rounded-lg px-4 py-3 text-slate-700 font-medium hover:bg-slate-50 hover:border-slate-400 transition-colors duration-200"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continuer avec Google
          </button>
        </div>
      </div>
    </div>
  );
}
